import React from 'react';

import PropTypes from 'prop-types';
import { FaCheck } from 'react-icons/fa';
import styled from 'styled-components';

import { getRbidByFacetNameAndFacetOption } from '~/gridv1_1/utils/filters';
import { sizeByKey } from '~/shared/utils/sizeConstants';
import useGender from '~/shared/utils/useGender';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

const DynamicFormattedMessage = FormattedMessage;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const CheckboxBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  appearance: none;
  width: 20px;
  height: 20px;
  background-color: white;
  cursor: pointer;
  ${({ theme }) => theme.gridFilterStyles.inputStyle};

  &:checked {
    border-color: ${({ theme }) =>
      theme.colors.gridSelectedFilterBackgroundColor};
    background-color: ${({ theme }) =>
      theme.colors.gridSelectedFilterBackgroundColor};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme }) => theme.colors.gridSwatchInputCheckedFocus};
    outline-offset: 2px;
  }
`;

const CheckIcon = styled(FaCheck)`
  pointer-events: none;
  position: absolute;
  inset: 0;
  margin: auto;
  color: white;
  font-size: 10px;
  opacity: 0;
  transition: opacity 0.2s;

  ${StyledCheckbox}:checked + & {
    opacity: 1;
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 400;
  color: black;
  line-height: 1;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;
  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle};
`;

const SuperScriptLabel = styled.sup`
  font-size: 10px;
  opacity: 0.75;
`;

function CheckboxInput({
  id,
  name,
  label,
  superScriptLabel,
  superScriptCategory,
  checked,
  onChange,
  filterName,
}) {
  const domain = useDomain();
  const { gender } = useGender();
  const localizedLabel =
    sizeByKey({
      domain,
      gender,
      category: filterName,
      key: label,
    }) || label;
  const snakecaseFilterName = filterName.replace(/-/g, '_').toLowerCase();
  const slugifiedRBLabel = getRbidByFacetNameAndFacetOption(
    snakecaseFilterName,
    localizedLabel
  );

  // This is currently intended for SXF Custom Bra Filters only
  // Force female for Bra sizes, for the edge case where a user is male browsing
  const localizedSuperScriptLabel =
    superScriptLabel &&
    superScriptCategory &&
    sizeByKey({
      domain,
      gender: 'F',
      category: superScriptCategory,
      key: superScriptLabel,
    });

  return (
    <CheckboxWrapper data-autotag={`filter-${filterName}-option`}>
      <CheckboxBox>
        <StyledCheckbox
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
        />
        <CheckIcon aria-hidden="true" />
      </CheckboxBox>
      <Label htmlFor={id}>
        {localizedSuperScriptLabel && (
          <SuperScriptLabel>{localizedSuperScriptLabel}</SuperScriptLabel>
        )}
        <DynamicFormattedMessage
          id={`site_product_filter.${slugifiedRBLabel}`}
          defaultMessage={localizedLabel}
        />
      </Label>
    </CheckboxWrapper>
  );
}

CheckboxInput.propTypes = {
  checked: PropTypes.bool.isRequired,
  filterName: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  superScriptCategory: PropTypes.string,
  superScriptLabel: PropTypes.string,
};

export default CheckboxInput;
